/**
 * AdminMRRPanel — Tableau de bord des revenus récurrents (MRR).
 * Snapshot temps réel (abonnés actifs × prix du plan) + évolution des ventes
 * mensuelles pour Communauté / Transformation / VIP (plans app récurrents).
 * Distinct du "CA mensuel" (onglet Facturation) qui couvre le présentiel.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, Crown, Users, Sparkles, Wallet, Clock, Send, CheckCircle2, UserMinus, Utensils, Dumbbell, MessageCircleHeart } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  communaute: { label: 'Communauté', color: '#ec4899', icon: Users },
  transformation: { label: 'Transformation', color: '#a855f7', icon: Sparkles },
  vip: { label: 'VIP Hybrid', color: '#8b5cf6', icon: Crown },
  monthly: { label: 'Suivi Pro (mensuel)', color: '#FBBF24', icon: Wallet },
  yearly: { label: 'Suivi Pro (annuel)', color: '#FBBF24', icon: Wallet },
  solo: { label: 'Suivi Pro+', color: '#38bdf8', icon: Wallet },
};

const ExpiringSubscriptions = ({ csrfToken }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/subscriptions/expiring?days=7`, { credentials: 'include' });
      if (res.ok) setItems((await res.json()).items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendReminder = async (email) => {
    setSending(email);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/subscriptions/send-expiry-reminder`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Rappel envoyé à ${email}`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-6" data-testid="expiring-subs-loading"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>;
  }
  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="expiring-subscriptions-card">
      <h3 className="text-amber-200 font-semibold text-sm inline-flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4" /> {items.length} abonnement{items.length > 1 ? 's' : ''} arrivant à échéance (7 jours)
      </h3>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.email} className="flex items-center justify-between gap-3 bg-black/20 rounded-xl px-3 py-2" data-testid={`expiring-sub-${it.email}`}>
            <div className="text-sm min-w-0">
              <span className="text-white font-semibold">{it.name || it.email}</span>{' '}
              <span className="text-zinc-500">· {PLAN_META[it.plan]?.label || it.plan} · dans {it.days_left}j</span>
              {it.last_reminder_sent_at && <span className="text-zinc-600 text-xs"> · rappel déjà envoyé</span>}
            </div>
            <button
              onClick={() => sendReminder(it.email)}
              disabled={sending === it.email}
              className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`send-expiry-reminder-${it.email}`}
            >
              {sending === it.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {it.last_reminder_sent_at ? 'Renvoyer' : 'Envoyer un rappel'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const NpsWidget = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/nps-dashboard?days=180`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => {});
  }, []);
  if (!data) return null;
  const scoreColor = data.nps_score === null ? 'text-zinc-400' : data.nps_score >= 50 ? 'text-emerald-400' : data.nps_score >= 0 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="nps-dashboard-card">
      <h3 className="text-white font-semibold inline-flex items-center gap-2 text-sm mb-4">
        <MessageCircleHeart className="w-4 h-4 text-primary" /> Satisfaction client (NPS) — 6 derniers mois
      </h3>
      {data.total_responses === 0 ? (
        <div className="py-6 text-center text-zinc-500 text-sm" data-testid="nps-no-responses">Aucune réponse pour le moment.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl bg-black/30 border border-white/10 p-3" data-testid="nps-score-value">
              <div className={`text-2xl font-black ${scoreColor}`}>{data.nps_score}</div>
              <p className="text-zinc-500 text-[11px] mt-0.5">Score NPS (-100 à +100)</p>
            </div>
            <div className="rounded-xl bg-black/30 border border-white/10 p-3">
              <div className="text-lg font-bold text-emerald-400">{data.promoters}</div>
              <p className="text-zinc-500 text-[11px] mt-0.5">Promoteurs (9-10)</p>
            </div>
            <div className="rounded-xl bg-black/30 border border-white/10 p-3">
              <div className="text-lg font-bold text-amber-400">{data.passives}</div>
              <p className="text-zinc-500 text-[11px] mt-0.5">Passifs (7-8)</p>
            </div>
            <div className="rounded-xl bg-black/30 border border-white/10 p-3">
              <div className="text-lg font-bold text-red-400">{data.detractors}</div>
              <p className="text-zinc-500 text-[11px] mt-0.5">Détracteurs (0-6)</p>
            </div>
          </div>
          {data.recent_comments.length > 0 && (
            <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="nps-comments-list">
              {data.recent_comments.map((c, i) => (
                <div key={i} className="text-xs bg-white/5 rounded-lg px-3 py-2" data-testid={`nps-comment-${i}`}>
                  <span className="text-zinc-500 mr-1.5">{c.score}/10 ·</span>
                  <span className="text-zinc-300">{c.comment}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const AdminMRRPanel = ({ onLogout, csrfToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/mrr-dashboard?months=6`, { credentials: 'include' });
        if (res.status === 401) { onLogout?.(); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur chargement MRR');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onLogout]);

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="mrr-loading"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }
  if (error) {
    return <div className="text-center text-zinc-400 py-12" data-testid="mrr-error">{error}</div>;
  }

  const byPlan = data?.by_plan || {};
  const planKeys = Object.keys(PLAN_META).filter((k) => byPlan[k]);
  const hasSeriesData = (data?.series || []).some((s) => s.communaute > 0 || s.transformation > 0 || s.vip > 0);

  return (
    <div className="space-y-5" data-testid="admin-mrr-panel">
      <ExpiringSubscriptions csrfToken={csrfToken} />

      {/* Résumé */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 to-black p-4 col-span-2 sm:col-span-1" data-testid="mrr-total-card">
          <TrendingUp className="w-5 h-5 text-primary mb-2" />
          <div className="text-2xl font-black text-white">{data.total_mrr.toFixed(0)} <span className="text-sm font-semibold text-zinc-400">CHF/mois</span></div>
          <p className="text-zinc-500 text-xs mt-0.5">MRR total · {data.total_subscribers} abonné{data.total_subscribers > 1 ? 's' : ''} actif{data.total_subscribers > 1 ? 's' : ''}</p>
        </div>
        {planKeys.map((k) => {
          const meta = PLAN_META[k];
          const Icon = meta.icon;
          return (
            <div key={k} className="rounded-2xl border border-white/10 bg-zinc-900/60 p-4" data-testid={`mrr-plan-card-${k}`}>
              <Icon className="w-4 h-4 mb-2" style={{ color: meta.color }} />
              <div className="text-xl font-bold text-white">{byPlan[k].mrr.toFixed(0)} <span className="text-xs font-semibold text-zinc-500">CHF</span></div>
              <p className="text-zinc-500 text-xs mt-0.5">{meta.label} · {byPlan[k].count} abonné{byPlan[k].count > 1 ? 's' : ''}</p>
            </div>
          );
        })}
        {planKeys.length === 0 && (
          <div className="col-span-full text-zinc-500 text-sm py-2" data-testid="mrr-no-subscribers">Aucun abonné actif pour le moment.</div>
        )}
        <div
          className={`rounded-2xl border p-4 ${
            data.churn_rate_30d > 5 ? 'border-red-500/30 bg-red-500/5' : 'border-white/10 bg-zinc-900/60'
          }`}
          data-testid="churn-rate-card"
        >
          <UserMinus className={`w-4 h-4 mb-2 ${data.churn_rate_30d > 5 ? 'text-red-400' : 'text-zinc-400'}`} />
          <div className="text-xl font-bold text-white">{data.churn_rate_30d?.toFixed(1) ?? '0.0'} <span className="text-xs font-semibold text-zinc-500">%</span></div>
          <p className="text-zinc-500 text-xs mt-0.5">Désabonnement (30j) · {data.churned_last_30d || 0} annulation{(data.churned_last_30d || 0) > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Plans générés par semaine */}
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="plans-generated-chart-card">
        <h3 className="text-white font-semibold inline-flex items-center gap-2 text-sm mb-4">
          <Sparkles className="w-4 h-4 text-primary" /> Plans générés — 8 dernières semaines (alimentaires / musculation)
        </h3>
        {!(data.plans_series || []).some((w) => w.meal_plans > 0 || w.workout_programs > 0) ? (
          <div className="h-48 flex items-center justify-center text-zinc-500 text-sm text-center px-6">
            Aucun plan généré sur cette période.
          </div>
        ) : (
          <div className="h-56" data-testid="plans-generated-chart-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.plans_series} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="week_label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }} />
                <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12, color: '#fff' }}
                  labelStyle={{ color: '#fafafa', fontWeight: 600, marginBottom: 4 }}
                />
                <Legend wrapperStyle={{ color: '#a1a1aa', fontSize: 11, paddingTop: 8 }}
                  formatter={(v) => (v === 'meal_plans' ? 'Plans alimentaires' : 'Programmes musculation')} />
                <Line type="monotone" dataKey="meal_plans" stroke="#FBBF24" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="workout_programs" stroke="#a855f7" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <NpsWidget />

      {/* Évolution des ventes */}
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="mrr-chart-card">
        <h3 className="text-white font-semibold inline-flex items-center gap-2 text-sm mb-4">
          <TrendingUp className="w-4 h-4 text-primary" /> Nouvelles ventes — 6 derniers mois (Communauté / Transformation / VIP)
        </h3>
        {!hasSeriesData ? (
          <div className="h-56 flex items-center justify-center text-zinc-500 text-sm text-center px-6">
            Aucune vente Stripe enregistrée sur cette période (les accès attribués manuellement par l'admin n'apparaissent pas ici).
          </div>
        ) : (
          <div className="h-72" data-testid="mrr-chart-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="month_label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }} />
                <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                <Tooltip
                  cursor={{ fill: 'rgba(233,30,99,0.05)' }}
                  contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12, color: '#fff' }}
                  labelStyle={{ color: '#fafafa', fontWeight: 600, marginBottom: 4 }}
                  formatter={(value, name) => [`${value.toFixed(2)} CHF`, PLAN_META[name]?.label || name]}
                />
                <Legend wrapperStyle={{ color: '#a1a1aa', fontSize: 11, paddingTop: 8 }} formatter={(v) => PLAN_META[v]?.label || v} />
                <Bar dataKey="communaute" fill={PLAN_META.communaute.color} radius={[4, 4, 0, 0]} />
                <Bar dataKey="solo" fill={PLAN_META.solo.color} radius={[4, 4, 0, 0]} />
                <Bar dataKey="transformation" fill={PLAN_META.transformation.color} radius={[4, 4, 0, 0]} />
                <Bar dataKey="vip" fill={PLAN_META.vip.color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMRRPanel;
